import { QueryClientProvider } from "@tanstack/react-query";
import { GeographyFilterProvider } from "@/contexts/GeographyFilterContext";
import { GeographyFilterSheetHost } from "@/components/GeographicalFilterSheet";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { ModuleFlagsProvider, useModuleFlags } from "@/contexts/ModuleFlagsContext";
import { OnboardingProvider } from "@/components/onboarding/OnboardingProvider";
import { ScreenLockProvider } from "@/contexts/ScreenLockContext";

import AuthPage from "@/pages/auth-page";
import OiWorkspacePage from "@/pages/oi-workspace";
import RdWorkspacePage from "@/pages/rd-workspace";
import GoWorkspacePage from "@/pages/go-workspace";
import UdgspzWorkspacePage from "@/pages/udgspz-workspace";
import ProfilePage from "@/pages/profile-page";
import SettingsPage from "@/pages/settings-page";
import AnalyticsPage from "@/pages/analytics";
import AdminPage from "@/pages/admin";
import HelpPage from "@/pages/help";
import ObjectWizardPage from "@/pages/object-wizard";
import WorkItemsPage from "@/pages/work-items";
import VisualizerWorkspacePage from "@/pages/visualizer-workspace";
import TacticalWorkspacePage from "@/pages/tactical-workspace";
import NotificationsPage from "@/pages/notifications-page";
import NotificationSettingsPage from "@/pages/notification-settings-page";
import ChatWorkspacePage from "@/pages/chat-workspace";

import { queryClient } from "@/lib/queryClient";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem("access_token");

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function defaultHomePath(flags: ReturnType<typeof useModuleFlags>["flags"]): string {
  if (flags.moduleOiEnabled) return "/oi";
  if (flags.moduleGoEnabled) return "/go";
  if (flags.moduleRdEnabled) return "/rd";
  if (flags.moduleUdgspzEnabled) return "/udgspz";
  if (flags.moduleVisualizerEnabled) return "/visualizer";
  if (flags.moduleTacticalEnabled) return "/tactical";
  if (flags.workItemsUiEnabled) return "/work-items";
  return "/settings";
}

function ModuleEnabledRoute({
  enabled,
  fallbackPath,
  children,
}: {
  enabled: boolean;
  fallbackPath: string;
  children: React.ReactNode;
}) {
  if (!enabled) {
    return <Navigate to={fallbackPath} replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  const { flags } = useModuleFlags();
  const home = defaultHomePath(flags);

  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/auth" element={<AuthPage />} />

      <Route
        path="/oi"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleOiEnabled} fallbackPath={home}>
              <OiWorkspacePage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rd"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleRdEnabled} fallbackPath={home}>
              <RdWorkspacePage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/go"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleGoEnabled} fallbackPath={home}>
              <GoWorkspacePage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/udgspz"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleUdgspzEnabled} fallbackPath={home}>
              <UdgspzWorkspacePage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/settings"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/objects/wizard"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleOiEnabled} fallbackPath={home}>
              <ObjectWizardPage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visualizer"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleVisualizerEnabled} fallbackPath={home}>
              <VisualizerWorkspacePage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tactical"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleTacticalEnabled} fallbackPath={home}>
              <TacticalWorkspacePage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ModuleEnabledRoute enabled={flags.moduleChatEnabled} fallbackPath={home}>
              <ChatWorkspacePage />
            </ModuleEnabledRoute>
          </ProtectedRoute>
        }
      />
      {flags.workItemsUiEnabled && (
        <Route
          path="/work-items"
          element={
            <ProtectedRoute>
              <WorkItemsPage />
            </ProtectedRoute>
          }
        />
      )}

      <Route path="/" element={<Navigate to={home} replace />} />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ModuleFlagsProvider>
          <TooltipProvider>
            <GeographyFilterProvider>
              <BrowserRouter>
                <ScreenLockProvider>
                  <AppRoutes />
                  <OnboardingProvider />
                  <GeographyFilterSheetHost />
                </ScreenLockProvider>
              </BrowserRouter>
            </GeographyFilterProvider>
            <Toaster />
            <PWAUpdatePrompt />
          </TooltipProvider>
        </ModuleFlagsProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
}

export default App;
